import { DEFAULT_COUNTRY_CODE } from 'lib/core/resources/constants'

/**
 * Env-based CommerceHub wallet config reader (architecture §3).
 *
 * IMPORTANT: only NON-secret identifiers may live here - CRA inlines every
 * `REACT_APP_*` value into the public client bundle. The Apple `.p12`
 * merchant certificate / private key stays server-side only (see FONTOS.md).
 */

export type GooglePayEnvironment = 'TEST' | 'PRODUCTION'

export interface CommerceHubApplePayConfig {
  merchantId: string
  displayName: string
  networks: string[]
  capabilities: string[]
  countryCode: string
  /** Backend merchant-validation proxy endpoint. Empty when the backend GAP (FONTOS.md) is unresolved. */
  validationUrl: string
}

export interface CommerceHubGooglePayConfig {
  merchantId: string
  merchantName: string
  gateway: string
  gatewayMerchantId: string
  networks: string[]
  countryCode: string
  environment: GooglePayEnvironment
}

const DEFAULT_APPLE_DISPLAY_NAME = 'Home Page'
const DEFAULT_APPLE_NETWORKS = ['visa', 'masterCard', 'amex', 'discover']
const DEFAULT_APPLE_CAPABILITIES = ['supports3DS', 'supportsCredit', 'supportsDebit']
const DEFAULT_GOOGLE_NETWORKS = ['VISA', 'MASTERCARD', 'AMEX', 'DISCOVER']
const DEFAULT_GOOGLE_GATEWAY = 'commercehub'

const csvToList = (value: string | undefined, fallback: string[]): string[] => {
  if (!value) {
    return fallback
  }

  const parsed = value.split(',').map(item => item.trim()).filter(Boolean)
  return parsed.length ? parsed : fallback
}

// NOTE: intentionally not `??` - `.env` placeholders are empty strings (not
// undefined), which `??` would not replace with the default.
const withDefault = <T extends string>(value: T | undefined, fallback: T): T => {
  if (!value) {
    return fallback
  }

  return value
}

export const getCommerceHubApplePayConfig = (): CommerceHubApplePayConfig => ({
  merchantId: process.env.REACT_APP_COMMERCEHUB_APPLE_MERCHANT_ID ?? '',
  displayName: withDefault(process.env.REACT_APP_COMMERCEHUB_APPLE_DISPLAY_NAME, DEFAULT_APPLE_DISPLAY_NAME),
  networks: csvToList(process.env.REACT_APP_COMMERCEHUB_APPLE_NETWORKS, DEFAULT_APPLE_NETWORKS),
  capabilities: csvToList(process.env.REACT_APP_COMMERCEHUB_APPLE_CAPABILITIES, DEFAULT_APPLE_CAPABILITIES),
  countryCode: withDefault(process.env.REACT_APP_COMMERCEHUB_APPLE_COUNTRY, DEFAULT_COUNTRY_CODE),
  validationUrl: process.env.REACT_APP_COMMERCEHUB_APPLE_VALIDATION_URL ?? ''
})

export const getCommerceHubGooglePayConfig = (): CommerceHubGooglePayConfig => ({
  merchantId: process.env.REACT_APP_COMMERCEHUB_GOOGLE_MERCHANT_ID ?? '',
  merchantName: process.env.REACT_APP_TITLE ?? '',
  gateway: withDefault(process.env.REACT_APP_COMMERCEHUB_GOOGLE_GATEWAY, DEFAULT_GOOGLE_GATEWAY),
  gatewayMerchantId: process.env.REACT_APP_COMMERCEHUB_GOOGLE_GATEWAY_MERCHANT_ID ?? '',
  networks: csvToList(process.env.REACT_APP_COMMERCEHUB_GOOGLE_NETWORKS, DEFAULT_GOOGLE_NETWORKS),
  countryCode: withDefault(process.env.REACT_APP_COMMERCEHUB_GOOGLE_COUNTRY, DEFAULT_COUNTRY_CODE),
  environment: withDefault(process.env.REACT_APP_COMMERCEHUB_ENVIRONMENT as GooglePayEnvironment, 'TEST')
})
